'use client';

import { t } from 'mobx-state-tree';

import { PropertyType } from '../propertyType';

import { PropertyBase } from './propertyBase';

import type { Instance } from 'mobx-state-tree';

/** Model for button property - invokes a method when clicked */
export const ButtonProperty = t.compose('ButtonProperty', PropertyBase,
  t.model({
    type: t.literal(PropertyType.Button),
    /** The method name to invoke when button is clicked */
    methodName: t.string,
    /** Button label text */
    label: t.string,
    /** Optional tooltip text */
    tooltip: t.optional(t.string, ''),
  })
).volatile(() => ({
  /** Whether the button is currently in a loading/executing state */
  isExecuting: false,
})).actions(self => {
  // Store a reference to the actions object for use in closures
  let actionsRef: { resetExecuting: () => void } | null = null;

  const actions = {
  /**
   * Trigger the button action.
   * This notifies the property change system which will invoke the method on the target.
   */
    trigger(): void {
      if (self.isExecuting) return;

      self.isExecuting = true;

      // Notify value change with 'true' to trigger method invocation
      // The actual invocation happens in the onChange handler
      self.notifyValueChange(self.path, true, { fromPanel: true, property: self as Instance<typeof ButtonProperty>, initial: false });

      // Reset executing state after a short delay using the stored action reference
      setTimeout(() => {
        actionsRef?.resetExecuting();
      }, 100);
    },
    resetExecuting(): void {
      self.isExecuting = false;
    },
    setLabel(label: string) {
      self.label = label;
    },
    setTooltip(tooltip: string) {
      self.tooltip = tooltip;
    },
  };

  // Store reference for use in trigger's setTimeout
  actionsRef = actions;

  return actions;
});

export type IButtonProperty = Instance<typeof ButtonProperty>;
